import React, { useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import api from "@/lib/api";
import { motion, AnimatePresence } from "framer-motion";
import { Camera, UploadSimple, Sparkle, Spinner, ForkKnife, CheckCircle, X, PencilSimple } from "@phosphor-icons/react";
import { toast } from "sonner";

const meals = ["breakfast", "lunch", "dinner", "snack"];

export default function Scanner() {
  const navigate = useNavigate();
  const fileRef = useRef();
  const [image, setImage] = useState(null); // data url
  const [description, setDescription] = useState("");
  const [analyzing, setAnalyzing] = useState(false);
  const [result, setResult] = useState(null);
  const [mealType, setMealType] = useState("lunch");
  const [saving, setSaving] = useState(false);

  const onFile = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (!["image/jpeg", "image/png", "image/webp"].includes(file.type)) {
      toast.error("Use a JPEG, PNG or WEBP image"); return;
    }
    const reader = new FileReader();
    reader.onload = () => { setImage(reader.result); setResult(null); };
    reader.readAsDataURL(file);
  };

  const analyze = async () => {
    if (!image && !description.trim()) { toast.error("Add a photo or describe your meal"); return; }
    setAnalyzing(true);
    setResult(null);
    try {
      const payload = {};
      if (image) payload.image_base64 = image;
      if (description.trim()) payload.description = description.trim();
      const r = await api.post("/nutrition/analyze", payload);
      setResult(r.data);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "AI analysis failed. Try again.");
    } finally { setAnalyzing(false); }
  };

  const saveLog = async () => {
    if (!result) return;
    setSaving(true);
    try {
      await api.post("/nutrition/log", {
        name: result.name, calories: result.calories, protein: result.protein,
        carbs: result.carbs, fat: result.fat, meal_type: mealType,
        image_base64: image || null,
      });
      toast.success("Logged to your diary");
      navigate("/app/wellness");
    } catch { toast.error("Could not save"); } finally { setSaving(false); }
  };

  const reset = () => { setImage(null); setDescription(""); setResult(null); };

  return (
    <div className="px-4 md:px-8 py-8 md:py-12 max-w-4xl mx-auto">
      <div className="mb-8">
        <div className="text-xs uppercase tracking-[0.2em] text-[#737373] font-semibold mb-1 flex items-center gap-2">
          <Sparkle size={14} weight="fill" color="#FF3B30" /> AI Food Scanner
        </div>
        <h1 className="font-head text-4xl md:text-5xl font-black uppercase tracking-tighter leading-none">Scan your <span className="text-[#FF3B30]">meal</span></h1>
        <p className="text-[#A3A3A3] mt-2">Upload a photo or describe what you ate — AI estimates the macros.</p>
      </div>

      {/* Dropzone */}
      <div className="relative rounded-sm p-[2px] overflow-hidden mb-5">
        {analyzing && <div className="absolute inset-0 tracing-border rounded-sm" />}
        <div
          onClick={() => !analyzing && fileRef.current?.click()}
          data-testid="scanner-dropzone"
          className={`relative bg-[#141414] border border-white/15 rounded-sm cursor-pointer transition-colors hover:border-[#FF3B30]/50 ${image ? "p-3" : "p-12"} flex flex-col items-center justify-center text-center`}
        >
          <input ref={fileRef} type="file" accept="image/png,image/jpeg,image/webp" hidden onChange={onFile} data-testid="scanner-file-input" />
          {image ? (
            <div className="relative w-full">
              <img src={image} alt="meal" className={`w-full max-h-80 object-contain rounded-sm ${analyzing ? "scanning" : ""}`} />
              {!analyzing && (
                <button onClick={(e) => { e.stopPropagation(); setImage(null); setResult(null); }} data-testid="remove-image-button"
                  className="absolute top-2 right-2 bg-black/70 hover:bg-[#FF3B30] text-white rounded-sm p-2 transition-colors">
                  <X size={16} weight="bold" />
                </button>
              )}
            </div>
          ) : (
            <>
              <div className="w-16 h-16 rounded-sm bg-[#FF3B30]/10 flex items-center justify-center mb-4">
                <Camera size={32} weight="duotone" color="#FF3B30" />
              </div>
              <p className="font-head text-xl font-bold uppercase tracking-tight mb-1">Tap to add a photo</p>
              <p className="text-sm text-[#737373]">JPEG, PNG or WEBP</p>
            </>
          )}
        </div>
      </div>

      {/* Description */}
      <div className="mb-5">
        <label className="block text-xs uppercase tracking-[0.2em] text-[#737373] font-semibold mb-2 flex items-center gap-2">
          <PencilSimple size={14} weight="bold" /> Or describe it
        </label>
        <textarea value={description} onChange={(e) => setDescription(e.target.value)} rows={2} data-testid="scanner-description-input"
          placeholder="e.g. Grilled chicken breast, 200g rice and steamed broccoli"
          className="w-full bg-[#0A0A0A] border border-white/20 text-white focus:border-[#FF3B30] focus:ring-1 focus:ring-[#FF3B30] rounded-sm p-4 placeholder-gray-600 outline-none transition-colors resize-none" />
      </div>

      <button onClick={analyze} disabled={analyzing} data-testid="analyze-button"
        className="w-full flex items-center justify-center gap-2 bg-[#FF3B30] hover:bg-[#FF5C53] disabled:opacity-60 text-white font-bold uppercase tracking-widest px-8 py-4 rounded-sm transition-colors mb-8">
        {analyzing ? <><Spinner size={20} className="animate-spin" /> Analyzing…</> : <><Sparkle size={20} weight="fill" /> Analyze Nutrition</>}
      </button>

      {/* Result */}
      <AnimatePresence>
        {result && (
          <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0 }}
            className="bg-[#141414] border border-white/10 rounded-sm p-6 md:p-8" data-testid="analysis-result">
            <div className="flex items-start justify-between mb-6">
              <div>
                <div className="flex items-center gap-2 text-xs uppercase tracking-[0.2em] text-[#34C759] font-semibold mb-1">
                  <CheckCircle size={14} weight="fill" /> Analysis complete · {result.confidence} confidence
                </div>
                <h2 className="font-head text-3xl font-bold uppercase tracking-tight" data-testid="result-name">{result.name}</h2>
              </div>
              <button onClick={reset} className="text-[#737373] hover:text-white" data-testid="reset-button"><X size={20} weight="bold" /></button>
            </div>

            <div className="grid grid-cols-4 gap-3 mb-6">
              {[
                { l: "Calories", v: result.calories, u: "", c: "#FFFFFF", tid: "result-calories" },
                { l: "Protein", v: result.protein, u: "g", c: "#FF3B30", tid: "result-protein" },
                { l: "Carbs", v: result.carbs, u: "g", c: "#FF9F0A", tid: "result-carbs" },
                { l: "Fat", v: result.fat, u: "g", c: "#34C759", tid: "result-fat" },
              ].map((m) => (
                <div key={m.l} className="bg-[#0A0A0A] border border-white/10 rounded-sm p-4 text-center">
                  <div className="font-mono text-2xl md:text-3xl font-black" style={{ color: m.c }} data-testid={m.tid}>{Math.round(m.v)}{m.u}</div>
                  <div className="text-[10px] uppercase tracking-widest text-[#737373] mt-1">{m.l}</div>
                </div>
              ))}
            </div>

            {result.items?.length > 1 && (
              <div className="mb-6">
                <div className="text-xs uppercase tracking-[0.2em] text-[#737373] font-semibold mb-3">Breakdown</div>
                <div className="space-y-2">
                  {result.items.map((it, i) => (
                    <div key={i} className="flex items-center justify-between text-sm border-b border-white/5 pb-2">
                      <span className="text-white">{it.name}</span>
                      <span className="font-mono text-[#A3A3A3]">{Math.round(it.calories)} kcal</span>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {result.notes && (
              <div className="bg-[#FF3B30]/10 border border-[#FF3B30]/30 rounded-sm p-4 mb-6 text-sm text-[#FFB3AE]">
                <ForkKnife size={16} weight="fill" className="inline mr-2" />{result.notes}
              </div>
            )}

            <div className="mb-5">
              <div className="text-xs uppercase tracking-[0.2em] text-[#737373] font-semibold mb-3">Meal type</div>
              <div className="grid grid-cols-4 gap-2">
                {meals.map((m) => (
                  <button key={m} onClick={() => setMealType(m)} data-testid={`meal-type-${m}`}
                    className={`py-3 rounded-sm text-xs uppercase tracking-widest font-semibold transition-colors ${
                      mealType === m ? "bg-[#FF3B30] text-white" : "bg-[#0A0A0A] border border-white/15 text-[#A3A3A3] hover:border-white/40"
                    }`}>{m}</button>
                ))}
              </div>
            </div>

            <button onClick={saveLog} disabled={saving} data-testid="save-log-button"
              className="w-full flex items-center justify-center gap-2 bg-[#34C759] hover:bg-[#2faf4d] disabled:opacity-60 text-black font-bold uppercase tracking-widest px-8 py-4 rounded-sm transition-colors">
              {saving ? <Spinner size={20} className="animate-spin" /> : <>Log this meal</>}
            </button>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}
